/**
 * BeneficiaryEditor — full CRUD on IRA plan beneficiaries.
 *
 * Gotchas (modules digest §RIM):
 *   - Beneficiaries on IRAs go through `Customer/TaxPlnBenfAdd` + TaxPlnMod —
 *     NOT CustRelAdd.
 *   - Primary sum must equal 100.00, Contingent sum must equal 100.00 (separate buckets).
 *   - Field 279 client-side cap [0, 100] — Core Director is permissive; we guard.
 *   - SECURE Act inherited-IRA banner is shown when the PLAN is inherited.
 *   - No joint owners on IRAs (enforced on the plan itself, not here).
 */
import { useMemo, useState } from 'react';
import type { RawJxResponse } from '@retail-ops/shared-types';
import { Button, Pill } from '@/components/primitives';
import { useApi } from './hooks/useApi';
import { useEmitEvent } from './hooks/useEmitEvent';
import { BENF_DESIG, BENEFICIARY_PERCENT_MAX, BENEFICIARY_PERCENT_MIN } from './constants';

export interface BeneficiaryEditorProps {
  planNumber: string;
  /** Existing beneficiaries loaded from TaxPlnBenfSrch; may be empty. */
  initial: ReadonlyArray<BeneficiaryDraft>;
  /** When true the plan is an Inherited IRA (adds SECURE Act banner). */
  inherited?: boolean;
  readOnly?: boolean;
}

export interface BeneficiaryDraft {
  tempId: string;        // local identity — NOT the JHA BenfSeqNum
  benfDesigId: 1 | 2;    // 1 primary, 2 contingent
  name: string;
  relationship: string;
  percent: number;
  dob?: string;
  isNew?: boolean;
}

function newDraft(desig: 1 | 2): BeneficiaryDraft {
  return {
    tempId: `new-${Math.random().toString(36).slice(2, 10)}`,
    benfDesigId: desig,
    name: '',
    relationship: '',
    percent: 0,
    isNew: true,
  };
}

interface Totals {
  primary: number;
  contingent: number;
}

function totalsFor(list: ReadonlyArray<BeneficiaryDraft>): Totals {
  return list.reduce<Totals>(
    (acc, b) => ({
      primary: b.benfDesigId === BENF_DESIG.primary ? acc.primary + b.percent : acc.primary,
      contingent: b.benfDesigId === BENF_DESIG.contingent ? acc.contingent + b.percent : acc.contingent,
    }),
    { primary: 0, contingent: 0 },
  );
}

export function BeneficiaryEditor({
  planNumber,
  initial,
  inherited = false,
  readOnly = false,
}: BeneficiaryEditorProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const emit = useEmitEvent();

  const [rows, setRows] = useState<BeneficiaryDraft[]>(() => initial.map((b) => ({ ...b })));
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const totals = useMemo(() => totalsFor(rows), [rows]);

  const primaryCount = rows.filter((r) => r.benfDesigId === BENF_DESIG.primary).length;
  const contingentCount = rows.filter((r) => r.benfDesigId === BENF_DESIG.contingent).length;

  const primaryValid = primaryCount === 0 || Math.abs(totals.primary - 100) < 0.001;
  const contingentValid = contingentCount === 0 || Math.abs(totals.contingent - 100) < 0.001;
  const allValid = primaryValid && contingentValid && rows.every((r) => r.name.trim().length > 0);

  const update = (tempId: string, patch: Partial<BeneficiaryDraft>): void => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...patch } : r)));
  };

  const remove = (tempId: string): void => {
    setRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const addPrimary = (): void => setRows((prev) => [...prev, newDraft(BENF_DESIG.primary)]);
  const addContingent = (): void => setRows((prev) => [...prev, newDraft(BENF_DESIG.contingent)]);

  const save = async (): Promise<void> => {
    if (!allValid || readOnly) return;
    setSaving(true);
    setSaveError(null);
    const signal = makeSignal();
    try {
      for (const r of rows) {
        // New beneficiaries go through TaxPlnBenfAdd; existing rows would
        // use TaxPlnBenfMod — for the first drop we only handle the add path.
        if (!r.isNew) continue;
        const res = await client.jxrl.taxPlnBenfAdd<{
          PlanNumber: string;
          BenfDesigId: number;
          Name: string;
          Relationship: string;
          Percent: number;
          Dob?: string;
        }>(
          {
            PlanNumber: planNumber,
            BenfDesigId: r.benfDesigId,
            Name: r.name.trim(),
            Relationship: r.relationship.trim(),
            Percent: r.percent,
            ...(r.dob ? { Dob: r.dob } : {}),
          },
          { signal },
        );
        const anyRes = res as RawJxResponse & { Success?: boolean; Error?: string };
        if (anyRes.Success === false || anyRes.Error) {
          throw new Error(anyRes.Error ?? 'TaxPlnBenfAdd failed');
        }
      }
      emit({
        eventType: 'rim.beneficiary.saved',
        eventCategory: 'cif-account',
        payload: { planNumber, count: rows.length },
      });
      // Mark all rows as saved (not new).
      setRows((prev) => prev.map((r) => ({ ...r, isNew: false })));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const renderGroup = (desig: 1 | 2, title: string): JSX.Element => {
    const group = rows.filter((r) => r.benfDesigId === desig);
    const sum = desig === BENF_DESIG.primary ? totals.primary : totals.contingent;
    const valid = desig === BENF_DESIG.primary ? primaryValid : contingentValid;
    const addFn = desig === BENF_DESIG.primary ? addPrimary : addContingent;

    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: 'var(--pcb-blue-dark)' }}>{title}</h3>
          <span className={`rim-benef-totals ${valid ? 'is-valid' : sum === 0 ? '' : 'is-invalid'}`.trim()}>
            Sum <strong style={{ fontFamily: 'var(--font-mono)' }}>{sum.toFixed(2)}%</strong>
            {!valid && sum !== 0 && (
              <span style={{ marginLeft: 'var(--sp-4)' }}>
                (required: exactly 100.00)
              </span>
            )}
            {valid && sum > 0 && <Pill variant="ok">valid</Pill>}
          </span>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={addFn} style={{ marginLeft: 'auto' }}>
              + Add beneficiary
            </Button>
          )}
        </div>

        {group.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
            No {title.toLowerCase()} beneficiaries.
          </div>
        )}

        {group.map((r) => {
          const invalid = r.percent < BENEFICIARY_PERCENT_MIN || r.percent > BENEFICIARY_PERCENT_MAX;
          return (
            <div key={r.tempId} className={`rim-benef-row ${invalid ? 'is-invalid' : ''}`.trim()}>
              <input
                className="rim-benef-field"
                placeholder="Full legal name"
                value={r.name}
                disabled={readOnly}
                onChange={(e) => update(r.tempId, { name: e.currentTarget.value })}
              />
              <input
                className="rim-benef-field"
                placeholder="Relationship"
                value={r.relationship}
                disabled={readOnly}
                onChange={(e) => update(r.tempId, { relationship: e.currentTarget.value })}
              />
              <input
                className="rim-benef-field"
                type="number"
                inputMode="decimal"
                min={BENEFICIARY_PERCENT_MIN}
                max={BENEFICIARY_PERCENT_MAX}
                step="0.01"
                placeholder="%"
                value={r.percent}
                disabled={readOnly}
                onChange={(e) => {
                  const next = Math.min(
                    BENEFICIARY_PERCENT_MAX,
                    Math.max(BENEFICIARY_PERCENT_MIN, Number.parseFloat(e.currentTarget.value) || 0),
                  );
                  update(r.tempId, { percent: next });
                }}
              />
              {!readOnly && (
                <Button variant="ghost" size="sm" onClick={() => remove(r.tempId)}>
                  Remove
                </Button>
              )}
            </div>
          );
        })}
      </section>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-7)' }}>
      {inherited && (
        <div className="rim-rmd-banner is-warn">
          <strong>SECURE Act — Inherited IRA.</strong>{' '}
          Non-eligible designated beneficiaries must complete distribution within 10 years of the original owner&apos;s death (2020+).
        </div>
      )}

      {renderGroup(BENF_DESIG.primary, 'Primary')}
      {renderGroup(BENF_DESIG.contingent, 'Contingent')}

      {saveError && (
        <div role="alert" style={{
          padding: 'var(--sp-5)',
          background: 'var(--err-bg)',
          color: 'var(--err-text)',
          border: '1px solid var(--pcb-red)',
          borderRadius: 'var(--r-md)',
          fontSize: 12,
        }}>{saveError}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          onClick={save}
          disabled={!allValid || saving || readOnly}
          title={!allValid ? 'Fix percent totals + names before saving' : undefined}
        >
          {saving ? 'Saving…' : 'Save beneficiaries'}
        </Button>
      </div>
    </div>
  );
}
